import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import cn from '@/utils/cn';
import { useSectionSearch } from './SectionTabs';
import SPAButton from '../shared/SPAButton';
import SPADataTable from '../shared/SPADataTable';
import SPADialog from '../shared/SPADialog';
import SPADropdown from '../shared/SPADropdown';
import RoutedSPADialog from '../shared/RoutedSPADialog';
import SPAPill from '../shared/SPAPill';

interface Member {
  uid: string;
  student_id: number | null;
  name: string | null;
  assigned: boolean;
  courses: string[];
  /** Who the LMS roster says this is, when the platform shares it. */
  lms?: { email?: string; name?: string; sis_id?: string };
}

interface UnlinkedStudent {
  student_id: number;
  name: string;
}

interface Payload {
  members?: Member[];
  unlinked_students?: UnlinkedStudent[];
  linked?: number;
  assigned?: number;
  error?: string;
}

interface Edit {
  uid: string;
  studentId: number;
  confirmed: boolean;
  unlink?: boolean;
}

// AIDEV-NOTE: The LMS user leads with the name and email the roster reports, so support can see who
// they are linking; the uid is what the link is actually made on and stays underneath. NRPS carries
// names only when the tool is granted PII, so without them the uid and courses are all there is.
const LmsUser = ({ member }: { member: Member }) => {
  const { email, name, sis_id: sisId } = member.lms ?? {};
  const details = [email, sisId && `SIS ${sisId}`].filter(Boolean).join(' · ');

  return (
    <>
      {name != null && <span className="tw-block tw-text-spa-primary">{name}</span>}
      {details !== '' && <span className="tw-block tw-text-spa-secondary">{details}</span>}
      <span
        className={cn(
          'tw-block tw-font-mono',
          name == null ? 'tw-text-sm tw-text-spa-primary' : 'tw-text-xs tw-text-spa-tertiary',
        )}
      >
        {member.uid}
      </span>
      <span className="tw-block tw-text-spa-secondary">
        {member.courses.length > 0 ? `course ${member.courses.join(', ')}` : 'no course'}
      </span>
    </>
  );
};

interface RowActions {
  busy: boolean;
  onLink: (uid: string, studentId: number) => void;
  onUnlink: (uid: string) => void;
  students: UnlinkedStudent[];
}

// AIDEV-NOTE: A menu, not an SPASelect. The select's menu has nowhere to go here: inline it is clipped
// by the table's scrolling frame, and portalled out of the dialog it is outside the modal (no pointer
// events, aria-hidden). Radix's menu layers inside the dialog like any nested Radix surface.
const LinkMenu = ({
  busy, member, onLink, students,
}: Omit<RowActions, 'onUnlink'> & { member: Member }) => (
  <SPADropdown>
    <SPADropdown.Trigger asChild>
      <SPAButton
        aria-label={`Link ${member.lms?.name ?? member.uid} to a student`}
        disabled={busy || students.length === 0}
        size="xs"
      >
        Link to…
      </SPAButton>
    </SPADropdown.Trigger>
    <SPADropdown.Content align="end" className="tw-max-h-72 tw-overflow-y-auto">
      {students.map(student => (
        <SPADropdown.Item key={student.student_id} onSelect={() => onLink(member.uid, student.student_id)}>
          {student.name}
        </SPADropdown.Item>
      ))}
    </SPADropdown.Content>
  </SPADropdown>
);

const column = createColumnHelper<Member>();

// Module-level, with the row actions on the table's meta: columns built per render give every cell a new
// component type, so each re-render remounted the rows and closed an open Link menu under the pointer.
const columns = [
  column.display({
    cell: ({ row }) => <LmsUser member={row.original} />,
    header: 'In the LMS',
    id: 'lms',
  }),
  column.display({
    cell: ({ row: { original: member } }) => (member.name == null
      ? <span className="tw-text-spa-tertiary">Not linked to anyone</span>
      : (
        <>
          <span className="tw-text-spa-primary">{member.name}</span>
          {!member.assigned && (
            <span className="tw-ml-2"><SPAPill tone="warning">not assigned here</SPAPill></span>
          )}
        </>
      )),
    header: 'Is this TCI student',
    id: 'student',
  }),
  column.display({
    cell: ({ row: { original: member }, table }) => {
      const {
        busy, onLink, onUnlink, students,
      } = table.options.meta as RowActions;

      return member.student_id != null
        ? (
          <SPAButton disabled={busy} size="xs" variant="danger-outline" onClick={() => onUnlink(member.uid)}>
            Unlink
          </SPAButton>
        )
        : <LinkMenu busy={busy} member={member} students={students} onLink={onLink} />;
    },
    header: () => <span className="tw-sr-only">Actions</span>,
    id: 'actions',
    meta: { align: 'right', width: 1 },
  }),
] as ColumnDef<Member, unknown>[];

const StudentLinks = () => {
  const { id: assignmentId = '' } = useParams();
  const sectionSearch = useSectionSearch();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Edit | null>(null);

  const { data, isLoading } = useQuery({
    queryFn: async (): Promise<Payload> => {
      const { data: body } = await axios.get(
        Routes.plato_api_sysadmin_grade_sync_student_links_path(assignmentId),
      );

      return body.data;
    },
    queryKey: ['gradeSyncStudentLinks', assignmentId],
  });

  const save = useMutation({
    mutationFn: async (edit: Edit) => axios.post(
      Routes.plato_api_sysadmin_grade_sync_update_student_link_path(assignmentId),
      { confirmed: edit.confirmed, student_id: edit.studentId, uid: edit.uid, unlink: edit.unlink },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeSyncStudentLinks', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignment', assignmentId] });
      setPending(null);
    },
  });

  const members = data?.members ?? [];
  const unlinked = data?.unlinked_students ?? [];
  const refusal = (save.error as { response?: { data?: { errors?: string[] } } })
    ?.response?.data?.errors?.[0];

  const onLink = (uid: string, studentId: number) => {
    const edit = { confirmed: false, studentId, uid };
    setPending(edit);
    save.mutate(edit);
  };

  const actions: RowActions = {
    busy: save.isLoading,
    onLink,
    onUnlink: uid => save.mutate({
      confirmed: true, studentId: 0, uid, unlink: true,
    }),
    students: unlinked,
  };

  return (
    <RoutedSPADialog returnPath={`..${sectionSearch}`}>
      <SPADialog.Content size="lg">
        <SPADialog.Header>
          <SPADialog.Title>Who the LMS users are in TCI</SPADialog.Title>
          <SPADialog.Description>
            A score is posted for an LMS user, not a TCI student, so this link is what decides whose
            gradebook row it lands in. A student with no link here can never receive a grade, however
            healthy the rest of the wiring looks.
          </SPADialog.Description>
        </SPADialog.Header>

        <SPADialog.Body>
          {data?.error != null && <p className="tw-m-0 tw-text-sm tw-text-spa-error">{data.error}</p>}
          {refusal != null && (
            <div className="tw-mb-3 tw-rounded tw-border tw-border-solid tw-border-spa-border tw-px-3 tw-py-2">
              <p className="tw-m-0 tw-text-sm tw-text-spa-error">{refusal}</p>
              {pending != null && refusal.includes('already linked') && (
                <div className="tw-mt-2 tw-flex tw-gap-2">
                  <SPAButton type="button" variant="secondary" onClick={() => setPending(null)}>
                    Leave it
                  </SPAButton>
                  <SPAButton
                    disabled={save.isLoading}
                    type="button"
                    variant="danger"
                    onClick={() => save.mutate({ ...pending, confirmed: true })}
                  >
                    Move the link
                  </SPAButton>
                </div>
              )}
            </div>
          )}

          {!isLoading && (
            <p className="tw-m-0 tw-mb-2 tw-text-sm tw-text-spa-secondary">
              {`${data?.linked ?? 0} of ${members.length} LMS users are linked. `}
              {unlinked.length > 0
                ? `${unlinked.length} assigned ${unlinked.length === 1 ? 'student has' : 'students have'} no LMS `
                  + 'identity at all.'
                : 'Every assigned student has one.'}
            </p>
          )}

          <SPADataTable
            columns={columns}
            data={members}
            defaultSort={[]}
            emptyMessage="No LMS users on this assignment's courses."
            isLoading={isLoading}
            loadingRows={4}
            meta={actions}
          />

          {unlinked.length > 0 && (
            <p className="tw-m-0 tw-mt-3 tw-text-sm tw-text-spa-secondary">
              {`Waiting for an identity: ${unlinked.map(s => s.name).join(', ')}`}
            </p>
          )}
        </SPADialog.Body>

        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton type="button" variant="secondary">Close</SPAButton>
          </SPADialog.Close>
        </SPADialog.Footer>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default StudentLinks;
